import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import {
  IdentityConciliationStatus,
  IdentityMatchUtils,
  IdentityVerificationStatus,
  StripeVerificationSessionSummaryDto,
  UserForAdminDto,
  UserId,
} from '@workspace/data-types';
import { AlertSnackBarService } from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import { tap } from 'rxjs';
import {
  IdentityReviewDialogCoreComponent,
  IdentityReviewDialogResult,
} from './identity-review-dialog.component';

@Component({
  selector: 'core-identity-review-banner',
  templateUrl: './identity-review-banner.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TitleCasePipe, RouterLink, MatButtonModule, MatIconModule, MatDialogModule],
})
export class IdentityReviewBannerCoreComponent {
  private readonly api = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly alertSnackbar = inject(AlertSnackBarService);
  private readonly dialog = inject(MatDialog);

  user = input.required<UserForAdminDto>();
  userUpdated = output<UserForAdminDto>();

  identityVerificationStatus = IdentityVerificationStatus;
  latestStripeSession = signal<StripeVerificationSessionSummaryDto | null>(null);
  isLoadingStripeSession = signal<boolean>(false);

  readonly stripeVerifiedIdentity = computed(() => {
    const session = this.latestStripeSession();
    if (!session) return null;

    const idData = session.identity;
    const metaDoc = (session.metadata as Record<string, unknown> | undefined)?.['document'] as
      Record<string, unknown> | undefined;

    const firstName = idData?.first_name || (metaDoc?.['first_name'] as string) || '';
    const lastName = idData?.last_name || (metaDoc?.['last_name'] as string) || '';

    if (!firstName && !lastName) return null;

    return {
      firstName,
      lastName,
      type: idData?.type || (metaDoc?.['type'] as string) || undefined,
      issuingCountry: idData?.issuing_country || (metaDoc?.['issuing_country'] as string) || undefined,
    };
  });

  readonly matchScore = computed<number | null>(() => {
    const sessionScore =
      this.latestStripeSession()?.matchScore ??
      this.user().metadata?.stripe?.verification?.sessions?.[0]?.matchScore;
    if (typeof sessionScore === 'number') {
      return sessionScore;
    }
    const stripe = this.stripeVerifiedIdentity();
    const u = this.user();
    if (!stripe || (!stripe.firstName && !stripe.lastName)) return null;
    return IdentityMatchUtils.calculateMatch(
      stripe.firstName || '',
      stripe.lastName || '',
      u.identity?.firstName || '',
      u.identity?.lastName || '',
    ).score;
  });

  readonly isReviewRequired = computed(() => {
    const u = this.user();
    if (u.identity?.verificationStatus === IdentityVerificationStatus.VERIFIED) {
      return false;
    }
    if (u.identity?.verificationStatus === IdentityVerificationStatus.REVIEW_REQUIRED) {
      return true;
    }
    const latestSession = u.metadata?.stripe?.verification?.sessions?.[0];
    if (
      latestSession &&
      latestSession.status === IdentityVerificationStatus.VERIFIED &&
      latestSession.conciliation === IdentityConciliationStatus.FAILED &&
      !latestSession.verifiedBy
    ) {
      return true;
    }
    return false;
  });

  constructor() {
    effect(() => {
      if (this.isReviewRequired()) {
        this.loadLatestStripeVerificationSession(this.user()._id);
      } else {
        this.latestStripeSession.set(null);
      }
    });
  }

  loadLatestStripeVerificationSession(userId: UserId): void {
    this.isLoadingStripeSession.set(true);
    this.api
      .admin()
      .user(userId)
      .latestStripeVerificationSession()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap({
          next: (session) => {
            this.latestStripeSession.set(session);
            this.isLoadingStripeSession.set(false);
          },
          error: () => {
            this.isLoadingStripeSession.set(false);
          },
        }),
      )
      .subscribe();
  }

  openReviewModal(): void {
    const dialogRef = this.dialog.open(IdentityReviewDialogCoreComponent, {
      data: {
        user: this.user(),
        stripeIdentity: this.stripeVerifiedIdentity(),
      },
      width: '560px',
      maxWidth: '95vw',
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((result: IdentityReviewDialogResult | null) => {
          if (result) {
            this.updateVerificationStatus(result.status, result.applyIdentityData);
          }
        }),
      )
      .subscribe();
  }

  private updateVerificationStatus(status: IdentityVerificationStatus, applyIdentityData = false): void {
    this.api
      .admin()
      .user(this.user()._id)
      .patch()
      .verificationStatus(status, applyIdentityData)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((updatedUser) => {
          const msg = applyIdentityData
            ? 'Profil mis à jour avec les informations de la pièce d’identité et validé'
            : 'Statut de vérification mis à jour avec succès';
          this.alertSnackbar.success({ message: msg });
          this.userUpdated.emit(updatedUser);
        }),
      )
      .subscribe();
  }
}
